import { useEffect } from 'react';

type DensityMode = 'compact' | 'comfortable' | 'spacious';

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

export const computeDensity = (viewportWidth: number, viewportHeight: number) => {
  // CSS pixels describe usable space. DPR and display scaling must not enlarge the UI.
  const mode: DensityMode = viewportWidth <= 1440 || viewportHeight <= 820
    ? 'compact'
    : viewportWidth >= 1920 && viewportHeight >= 960 ? 'spacious' : 'comfortable';
  const fontSize = 16;
  const shellMax = clamp(viewportWidth - 32, 320, 2440);
  const contentScale = 1;
  const panelPadding = mode === 'compact' ? 0.85 : mode === 'spacious' ? 1.04 : 0.92;
  const navWidth = viewportWidth <= 1440 ? 184 : 248;
  const sectionMinHeight = clamp(viewportHeight - 160, 280, 960);

  return {
    mode,
    fontSize,
    shellMax,
    contentScale,
    panelPadding,
    navWidth,
    sectionMinHeight
  };
};

export function useAdaptiveDensity() {
  useEffect(() => {
    const root = document.documentElement;

    const applyDensity = () => {
      const density = computeDensity(window.innerWidth, window.innerHeight);
      root.dataset.uiDensity = density.mode;
      root.style.setProperty('--font-size', `${density.fontSize}px`);
      root.style.setProperty('--aql-shell-max-width', `${density.shellMax}px`);
      root.style.setProperty('--aql-content-scale', `${density.contentScale}`);
      root.style.setProperty('--aql-panel-padding-factor', `${density.panelPadding}`);
      root.style.setProperty('--aql-nav-width', `${density.navWidth}px`);
      root.style.setProperty('--aql-section-min-height', `${density.sectionMinHeight}px`);
    };

    applyDensity();
    window.addEventListener('resize', applyDensity, { passive: true });
    window.visualViewport?.addEventListener('resize', applyDensity, { passive: true });

    return () => {
      window.removeEventListener('resize', applyDensity);
      window.visualViewport?.removeEventListener('resize', applyDensity);
    };
  }, []);
}
